import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnInit,
} from '@angular/core';
import { UserService } from '../../core/services/user.service';
import { HelpFormDTO } from '../../core/models/helpform.model';
import { LoaderService } from '../../core/loader.service';
import { AuthService } from '../../core/services/auth.service';
import {
  getInternationalPhoneForSubmission,
  isValidInternationalPhone,
} from '../utils/phone-number.util';

@Component({
  selector: 'app-help-bot',
  templateUrl: './help-bot.component.html',
  styleUrls: ['./help-bot.component.scss'],
})
export class HelpBotComponent implements OnInit {
  isModalOpen = false;
  isSuccessOpen = false;
  helpRequest: HelpFormDTO = new HelpFormDTO();
  countries: any[] = [];
  filteredCountries: any[] = [];
  selectedCountry: any = null;
  countryCode = '+1';
  countryDropdownOpen = false;
  countrySearchTerm = '';
  private countriesLoading = false;

  // Custom validation states
  phoneError = false;
  issueError = false;
  submitError = '';

  constructor(
    private userService: UserService,
    private loaderService: LoaderService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService
  ) {}

  ngOnInit(): void {}

  openHelpModal(): void {
    this.isModalOpen = true;
    this.isSuccessOpen = false;
    this.submitError = '';
    this.loadCountries();
    this.cdr.detectChanges();
  }

  closeHelpModal(): void {
    this.isModalOpen = false;
    this.countryDropdownOpen = false;
  }

  closeSuccessModal(): void {
    this.isSuccessOpen = false;
  }

  resetForm(): void {
    this.helpRequest = new HelpFormDTO();
    this.phoneError = false;
    this.issueError = false;
    this.submitError = '';
  }

  validatePhone(phone: string): boolean {
    return isValidInternationalPhone(phone, this.countryCode);
  }

  toggleCountryDropdown(event: Event): void {
    event.stopPropagation();
    this.countryDropdownOpen = !this.countryDropdownOpen;

    if (this.countryDropdownOpen) {
      this.countrySearchTerm = '';
      this.filteredCountries = this.countries;
      this.loadCountries();
    }
  }

  filterCountries(): void {
    const searchTerm = this.countrySearchTerm.trim().toLowerCase();
    if (!searchTerm) {
      this.filteredCountries = this.countries;
      return;
    }

    this.filteredCountries = this.countries.filter(
      (country) =>
        country.name?.toLowerCase().includes(searchTerm) ||
        country.callingCode?.includes(searchTerm)
    );
  }

  selectCountry(country: any): void {
    this.selectedCountry = country;
    this.countryCode = country.callingCode;
    this.countryDropdownOpen = false;
    this.countrySearchTerm = '';
    this.filteredCountries = this.countries;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.cx-phone-country')) {
      this.countryDropdownOpen = false;
    }
  }

  onSubmit(): void {
    // Validate required fields
    this.submitError = '';
    this.phoneError = !this.validatePhone(this.helpRequest.phoneNumber);
    this.issueError =
      !this.helpRequest.issueDescription ||
      this.helpRequest.issueDescription.trim() === '';

    if (this.phoneError || this.issueError) {
      return;
    }

    this.loaderService.show();

    const request = {
      ...this.helpRequest,
      phoneNumber: this.getPhoneNumberForSubmission(),
    };

    this.userService.submitHelpForm(request).subscribe({
      next: (res: any) => {
        this.loaderService.hide();
        if (res && res.success === 0) {
          this.isModalOpen = false;
          this.isSuccessOpen = true;
          this.resetForm();
        } else {
          this.submitError =
            res?.message || 'Something went wrong. Please try again.';
        }
      },
      error: (err) => {
        this.loaderService.hide();
        console.error('Error submitting help request:', err);
        this.submitError = 'An error occurred. Please try again.';
      },
    });
  }

  private loadCountries(): void {
    if (this.countries.length > 0 || this.countriesLoading) {
      return;
    }

    this.countriesLoading = true;
    this.userService.getCountries(true).subscribe({
      next: (res: any) => {
        this.countriesLoading = false;
        if (!res || res.success !== 0 || !Array.isArray(res.data)) {
          return;
        }

        this.countries = res.data;
        this.filteredCountries = res.data;
        this.selectInitialCountry();
      },
      error: () => {
        this.countriesLoading = false;
      },
    });
  }

  private selectInitialCountry(): void {
    const user = this.authService.getUser();
    const savedCountryCode =
      localStorage.getItem('countryCode') || user?.countryCode;
    const savedCountryIsoCode =
      localStorage.getItem('countryIsoCode') || user?.countryIsoCode;
    const savedCountry = this.countries.find(
      (country) =>
        country.callingCode === savedCountryCode &&
        country.countryIsoCode === savedCountryIsoCode
    );
    const unitedStates = this.countries.find(
      (country) =>
        country.callingCode === '+1' && country.countryIsoCode === 'US'
    );
    const callingCodeMatch = this.countries.find(
      (country) => country.callingCode === savedCountryCode
    );
    const preferredCallingCodeMatch =
      savedCountryCode === '+1'
        ? unitedStates ?? callingCodeMatch
        : callingCodeMatch;
    const selectedCountry =
      savedCountry ??
      preferredCallingCodeMatch ??
      unitedStates ??
      this.countries[0];

    if (selectedCountry) {
      this.selectedCountry = selectedCountry;
      this.countryCode = selectedCountry.callingCode;
    }
  }

  private getPhoneNumberForSubmission(): string {
    return getInternationalPhoneForSubmission(
      this.helpRequest.phoneNumber,
      this.countryCode
    );
  }
}
